import React from 'react';
import { Box, Button, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import {
  Link as ChainIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Build as BuildIcon,
  Close as CloseIcon,
  SelectAll as SelectAllIcon,
  DeselectIcon,
} from '@mui/icons-material';

const BatchOperationsBar = ({
  selectedCount,
  totalProcesses,
  onChain,
  onDelete,
  onAssignEmployee,
  onAssignMachine,
  onSelectAll,
  onDeselectAll,
  onCancel,
}) => {
  if (selectedCount === 0) return null;

  return (
    <Box className="batchOperationsBar">
      {/* Selection Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Chip
          label={`${selectedCount} ausgewählt`}
          className="selectedCount"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'inherit',
            fontWeight: 600,
            fontSize: '0.9rem',
          }}
        />

        {selectedCount < totalProcesses && (
          <Tooltip title="Alle auswählen">
            <IconButton
              size="small"
              onClick={onSelectAll}
              sx={{
                color: 'inherit',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <SelectAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {selectedCount > 0 && (
          <Tooltip title="Auswahl aufheben">
            <IconButton
              size="small"
              onClick={onDeselectAll}
              sx={{
                color: 'inherit',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {selectedCount >= 2 && (
          <Tooltip title="Ausgewählte Prozesse verketten">
            <Button
              variant="contained"
              size="small"
              startIcon={<ChainIcon />}
              onClick={onChain}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'inherit',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                },
              }}
            >
              Verketten
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Mitarbeiter zuweisen">
          <Button
            variant="contained"
            size="small"
            startIcon={<PeopleIcon />}
            onClick={onAssignEmployee}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
              },
            }}
          >
            Mitarbeiter
          </Button>
        </Tooltip>

        <Tooltip title="Maschine zuweisen">
          <Button
            variant="contained"
            size="small"
            startIcon={<BuildIcon />}
            onClick={onAssignMachine}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
              },
            }}
          >
            Maschine
          </Button>
        </Tooltip>

        <Tooltip title="Ausgewählte löschen">
          <Button
            variant="contained"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{
              bgcolor: 'rgba(244, 67, 54, 0.8)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(244, 67, 54, 1)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
              },
            }}
          >
            Löschen
          </Button>
        </Tooltip>
      </Box>

      {/* Cancel Button */}
      <Tooltip title="Batch-Modus beenden">
        <IconButton
          onClick={onCancel}
          sx={{
            color: 'inherit',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Add a component for the batch mode toggle button in top bar
export const BatchModeToggle = ({ batchMode, onToggle, selectedCount }) => {
  return (
    <Tooltip title={batchMode ? 'Batch-Modus beenden' : 'Batch-Modus aktivieren'}>
      <Button
        variant={batchMode ? 'contained' : 'outlined'}
        size="small"
        startIcon={<SelectAllIcon />}
        onClick={onToggle}
        className="topBarButton"
        sx={{
          bgcolor: batchMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)',
          '&:hover': {
            bgcolor: batchMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.25)',
          },
        }}
      >
        {batchMode ? 'Batch-Modus' : 'Batch'}
        {selectedCount > 0 && (
          <Chip
            label={selectedCount}
            size="small"
            sx={{
              ml: 1,
              height: 20,
              bgcolor: 'error.main',
              color: 'white',
            }}
          />
        )}
      </Button>
    </Tooltip>
  );
};

export default BatchOperationsBar;
